/**
 * The prestige channel's client words (AD-779 S3, `q-prestige-window-words-and-attunement-render`;
 * spec docs/systems/prestige-cosmetic-acquisition-2026-10.md §4 item 6 as amended by §11).
 *
 * Bound wording rules:
 * - The WINDOW reads "Currently available" (open) or "Returning when The Great Cloud wills it"
 *   (closed) — never a countdown (the window has no clock), never a price, a Buy control or a
 *   queue number.
 * - An OFFER has a real clock, so the offered player's own line names a true accept-by date.
 * - "Attuned" here is the prestige standing, never the shrine tether; the words say so.
 * - The Great Cloud is never shortened (brand rule; `prestigeWords.test.ts` guards every string).
 */

/** One `prestige[]` entry from `GET /api/store/catalogue` (backend `store/prestigeView.ts`). */
export interface PrestigeEntry {
  id: string;
  name: string;
  availability: 'open' | 'closed';
  attuned: boolean;
  owned: boolean;
  held?: true;
  offered?: { expiresAt: string };
}

export const WINDOW_OPEN = 'Currently available';
export const WINDOW_CLOSED = 'Returning when The Great Cloud wills it';
export const OWNED_LINE = 'Yours.';
export const HELD_LINE =
  'Yours, waiting on two-factor authentication. Turn it on, then claim it under Founder packs.';
/** The attuned-not-offered words for a line with no lore of its own (the prestige sense, R-16). */
export const ATTUNED_FALLBACK =
  'You stand attuned to this prestige line. Whether The Great Cloud wills it to you is not yours to hurry.';
export const UNANSWERED_LINE = 'An offer left unanswered passes to the next attuned player.';
export const ACCEPT_LABEL = 'Accept';
export const ACCEPTED_OWNED = 'Accepted. It is yours.';
export const ACCEPTED_HELD = 'Accepted. It is held for you until two-factor authentication is on.';

/** The attuned-not-offered words, in the line's own lore ("spark-attuned" is the Oracle's word). */
const ATTUNED_BY_LINE: Readonly<Record<string, string>> = {
  'oracle-prismatic-aura':
    'You are spark-attuned. Whether The Great Cloud wills it to you is not yours to hurry.',
};

/** The standing words for a caller not yet attuned, in the line's own lore. */
const NOT_ATTUNED_BY_LINE: Readonly<Record<string, string>> = {
  'oracle-prismatic-aura':
    'The Oracle of Sparks attunes its Exemplars to the Prismatic Aura. The Great Cloud decides who wears it.',
};
export const NOT_ATTUNED_FALLBACK =
  'This line is earned through standing with a faction, never bought. The Great Cloud decides who wears it.';

/** Every string this module can show — the brand guard's input. */
export const PRESTIGE_STRINGS: readonly string[] = [
  WINDOW_OPEN,
  WINDOW_CLOSED,
  OWNED_LINE,
  HELD_LINE,
  ATTUNED_FALLBACK,
  UNANSWERED_LINE,
  ACCEPT_LABEL,
  ACCEPTED_OWNED,
  ACCEPTED_HELD,
  NOT_ATTUNED_FALLBACK,
  ...Object.values(ATTUNED_BY_LINE),
  ...Object.values(NOT_ATTUNED_BY_LINE),
  ...['offerLapsed', 'banned', 'noOffer', ''].map((c) => describeAcceptRefusal(c)),
];

/** Read `prestige[]` off a catalogue response; anything malformed is dropped, never guessed. */
export function parsePrestige(catalogue: unknown): PrestigeEntry[] {
  if (!catalogue || typeof catalogue !== 'object') return [];
  const raw = (catalogue as { prestige?: unknown }).prestige;
  if (!Array.isArray(raw)) return [];
  const out: PrestigeEntry[] = [];
  for (const r of raw as unknown[]) {
    if (!r || typeof r !== 'object') continue;
    const e = r as Record<string, unknown>;
    if (typeof e.id !== 'string' || typeof e.name !== 'string') continue;
    if (e.availability !== 'open' && e.availability !== 'closed') continue;
    const entry: PrestigeEntry = {
      id: e.id,
      name: e.name,
      availability: e.availability,
      attuned: e.attuned === true,
      owned: e.owned === true,
    };
    if (e.held === true) entry.held = true;
    const offered = e.offered as { expiresAt?: unknown } | undefined;
    if (
      offered &&
      typeof offered.expiresAt === 'string' &&
      !Number.isNaN(Date.parse(offered.expiresAt))
    )
      entry.offered = { expiresAt: offered.expiresAt };
    out.push(entry);
  }
  return out;
}

/** The window's words — the same for every caller. */
export function windowWords(e: PrestigeEntry): string {
  return e.availability === 'open' ? WINDOW_OPEN : WINDOW_CLOSED;
}

/**
 * The hold's true end — date AND time with the zone (mini-check P2: a bare local date reads
 * "Accept by 25 October" while an accept that evening is refused). Never a countdown.
 */
export function acceptByDate(expiresAt: string, locale?: string, timeZone?: string): string {
  return new Date(expiresAt).toLocaleString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
}

/** The offer line, or null when the caller holds no live offer on this line. */
export function offerWords(e: PrestigeEntry, locale?: string, timeZone?: string): string | null {
  if (!e.offered || e.owned || e.held) return null;
  return `The Great Cloud offers ${e.name} to you. Accept by ${acceptByDate(e.offered.expiresAt, locale, timeZone)}. ${UNANSWERED_LINE}`;
}

/** The caller's own standing beneath the window. */
export function standingWords(e: PrestigeEntry, locale?: string, timeZone?: string): string {
  if (e.owned) return OWNED_LINE;
  if (e.held) return HELD_LINE;
  const offer = offerWords(e, locale, timeZone);
  if (offer) return offer;
  if (e.attuned) return ATTUNED_BY_LINE[e.id] ?? ATTUNED_FALLBACK;
  return NOT_ATTUNED_BY_LINE[e.id] ?? NOT_ATTUNED_FALLBACK;
}

/** The refusal words for an accept the server declined (S2 route: 404 · 410 · 403). */
export function describeAcceptRefusal(code: string): string {
  if (code === 'offerLapsed')
    return 'That offer has passed. It has gone to the next attuned player.';
  if (code === 'banned') return 'This account cannot accept offers while a suspension stands.';
  if (code === 'noOffer') return 'There is no offer on this line for you.';
  return 'The offer could not be accepted. Try again shortly.';
}
